import React from 'react';
import { FaEdit, FaFlickr, FaDropbox, FaGoogleDrive, FaExternalLinkAlt } from 'react-icons/fa';
import { LockClosedIcon, InformationCircleIcon } from '@heroicons/react/24/solid';

interface ProfileFieldProps {
  label: string;
  value: string;
  onEdit: () => void;
  locked?: boolean;
  type?: string;
  placeholder?: string;
  defaultLink?: string;
  viewText?: string;
  helpText?: string;
}

export const ProfileField: React.FC<ProfileFieldProps> = ({
  label,
  value,
  onEdit,
  locked = false,
  type = 'text',
  placeholder = 'Not set',
  defaultLink,
  viewText,
  helpText
}) => {
  const getIcon = () => {
    if (type !== 'url') return null;
    if (label.includes('Flickr')) return <FaFlickr className="h-4 w-4 text-blue-500" />;
    if (label.includes('Dropbox')) return <FaDropbox className="h-4 w-4 text-blue-500" />;
    if (label.includes('Google Drive')) return <FaGoogleDrive className="h-4 w-4 text-red-500" />;
    return null;
  };

  return (
    <div className="flex flex-col py-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
            {getIcon()}
            {label}
            {locked && (
              <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" />
            )}
          </label>
          <div className={`text-gray-500 text-sm ${locked ? 'opacity-50' : ''}`}>
            {value === undefined || value === '' ? placeholder : value}
          </div>
        </div>
        <button
          onClick={onEdit}
          className={`ml-2 p-1 ${
            locked 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-400 hover:text-gray-600'
          }`}
          disabled={locked}
        >
          <FaEdit className="h-4 w-4" />
        </button>
      </div>
      {!locked && (
        <div className="mt-1 ml-6 space-y-1">
          {type === 'url' && (
            <div>
              {value ? (
                <a
                  href={value.startsWith('http') ? value : `https://${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <FaExternalLinkAlt className="h-3 w-3" />
                  <span>View/Open</span>
                </a>
              ) : defaultLink && (
                <a
                  href={defaultLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <FaExternalLinkAlt className="h-3 w-3" />
                  <span>{viewText}</span>
                </a>
              )}
            </div>
          )}
          {helpText && (
            <div className="flex items-start gap-1">
              <InformationCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-500">{helpText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
